import type { CheckedState } from "@radix-ui/react-checkbox";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";

import { ChatGeneratedFileOption } from "./ChatGeneratedFileOption";
import type { IChatGeneratedFilesListProps } from "./types";

// NOTE: NSAI-5312 — the chat's files with a select-all box above them, the
// same tick-to-pick flow as the AI Generated Files table.
const ChatGeneratedFilesListComponent = ({
  files,
  totalCount,
  selectedFiles,
  selection,
  checkedState,
  onToggleFile,
  onSetAllSelected,
}: IChatGeneratedFilesListProps): JSX.Element => {
  const { t } = useTranslation("common");

  // NOTE: Radix reports a click on an indeterminate box as `true`, so a
  // partly ticked list becomes fully ticked.
  const handleToggleAll = useCallback(
    (checked: CheckedState): void => {
      onSetAllSelected(checked === true);
    },
    [onSetAllSelected],
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {/* NOTE: The same 1px border (transparent) and 12px inset the rows get
          from the list's border, its padding and their own, so the select-all
          box sits exactly above the row boxes. */}
      <div className="flex items-center justify-between gap-3 border border-transparent px-3">
        <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
          <Checkbox
            checked={checkedState}
            onCheckedChange={handleToggleAll}
            data-testid="chat-generated-files-select-all"
          />
          {t("generated_files.download_dialog.select_all")}
        </label>
        <span
          className="text-xs text-muted-foreground"
          aria-live="polite"
          data-testid="chat-generated-files-selected-count"
        >
          {t("generated_files.download_dialog.selected_of_total", {
            selected: selectedFiles.length,
            total: files.length,
          })}
        </span>
      </div>
      <div
        className="max-h-[min(20rem,50vh)] overflow-y-auto rounded-md border border-border p-1"
        data-testid="chat-generated-files-list"
      >
        {files.map((file) => (
          <ChatGeneratedFileOption
            key={file.uuid}
            file={file}
            isSelected={selection.has(file.uuid)}
            onToggle={onToggleFile}
          />
        ))}
      </div>
      {totalCount > files.length && (
        <p
          className="px-2 text-xs text-muted-foreground"
          data-testid="chat-generated-files-truncated"
        >
          {t("generated_files.download_dialog.truncated", {
            shown: files.length,
            total: totalCount,
          })}
        </p>
      )}
    </div>
  );
};

export const ChatGeneratedFilesList = memo(ChatGeneratedFilesListComponent);
